import {
  Box,
  BoxProps,
  Flex,
  HStack,
  Text,
  useMediaQuery,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useState } from 'react'
import { CurrencyEnum } from '_app/types/api'
import PriceText from '_app/components/PriceText'
import DiscountForm from 'modules/checkout/components/DiscountForm'

interface CartSummaryProps extends BoxProps {
  data: any;
  productsPriceValue: number | null;
  productsDiscountPriceValue?: number | null;
  productsPriceCurrency?: CurrencyEnum | null;
  cartPriceCurrency?: CurrencyEnum | null;
  setData?: any;
  freeDeliveryThresholdValue: number;
  freeDeliveryThresholdCurrency: CurrencyEnum;
}

const CartSummary: FC<CartSummaryProps> = ({
  data,
  productsPriceValue,
  productsDiscountPriceValue,
  productsPriceCurrency,
  freeDeliveryThresholdValue,
  freeDeliveryThresholdCurrency,
  width = '100%',
  setData,
  bgColor = '#fff',
  border = 'solid 1px #ede7e1',
  p = { base: 4, md: 6 },
  ...props
}) => {
  const { t } = useTranslation('cart')
  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)')
  const [_, setCollapseSummary] = useState<boolean>()
  const isFreeDelivery =
    productsPriceValue && productsPriceValue >= freeDeliveryThresholdValue

  useEffect(() => {
    setCollapseSummary(isLargerThan1024)
  }, [isLargerThan1024])

  return (
    <Box width={width} bgColor={bgColor} border={border} p={p} {...props}>
      <Flex justifyContent="space-between" mb={5} alignItems="center">
        <Text fontSize="16px" lineHeight="30px">
          {t('ORDER_SUMMARY')}
        </Text>
      </Flex>

      <Box>
        <Box mb={4} display="flex" justifyContent="space-between" flexWrap="wrap">
          <Text
            fontSize="16px"
            fontWeight="light"
            lineHeight="30px"
          >
            {t('PRODUCTS', { ns: 'common' })}
          </Text>
          {productsPriceCurrency && (
            <PriceText
              amount={{
                currency: productsPriceCurrency,
                value: productsPriceValue,
              }}
              floating
              minimumFractionDigits={2}
            />
          )}
        </Box>

        <Flex justify="space-between" borderBottom="solid 1px #ddd" pb="16px">
          <Text fontWeight={300}>{t('SHIPMENT')}</Text>
          <Text>---------</Text>
        </Flex>

        <Flex justify="space-between" py="16px" borderBottom="solid 1px #ddd">
          <Text fontWeight={300}>{t('TOTAL')}</Text>
          <PriceText
            amount={{
              currency: productsPriceCurrency,
              value: productsPriceValue,
            }}
            floating
            minimumFractionDigits={2}
            productsDiscountPriceValue={productsDiscountPriceValue}
          />
        </Flex>

        <Box
          mb={4}
          display="flex"
          fontSize="14px"
          lineHeight="24px"
        >
          {!!isFreeDelivery && <Text pt={4}>{t('FREE_SHIPMENT')}</Text>}
          {!isFreeDelivery && (
            <HStack pt={4}>
              <Text>{t('FOR_FREE_SHIPMENT_YOU_ARE_MISSING')}</Text>&nbsp;
              {freeDeliveryThresholdCurrency && freeDeliveryThresholdValue && (
                <PriceText
                  amount={{
                    currency: freeDeliveryThresholdCurrency,
                    value:
                      freeDeliveryThresholdValue - (productsPriceValue ?? 0),
                  }}
                  floating
                  minimumFractionDigits={2}
                />
              )}
            </HStack>
          )}
        </Box>

        <DiscountForm setData={setData} data={data} />
      </Box>
    </Box>
  )
}

export default CartSummary
